import { BlogArticle } from '../../components/blog/BlogArticle.tsx';
import { Link } from '../../components/Link.tsx';
import { MediaVideo } from '../../components/MediaVideo.tsx';
import { getApiBaseUrl } from '../../config.ts';
import { MCP_APP_DEMO_VIDEO } from '../../marketing-shots.ts';
import type { BlogPostMeta } from './posts.ts';

export const goalpostMcpAppPost: BlogPostMeta = {
  slug: 'goalpost-mcp-app',
  titleKey: 'blog.goalpost-mcp-app.title',
  summaryKey: 'blog.goalpost-mcp-app.summary',
  publishedOn: '2026-10-28',
};

export function GoalpostMcpApp() {
  const apiBase = getApiBaseUrl();
  const mcpUrl = apiBase ? `${apiBase}/api/mcp` : `${window.location.origin}/api/mcp`;

  return (
    <BlogArticle post={goalpostMcpAppPost}>
      <p className="mkt-eyebrow">TLDR</p>
      <ul>
        <li>Goalpost already lets AI tools and agents like Claude and ChatGPT read and edit your Goalpost documents through our MCP server.</li>
        <li>With support for MCP Apps, AI agents can now bring Goalpost's rich interface into your chat threads, not just text.</li>
      </ul>
      <h2 id="why">With AI and Goalpost, you choose your interface</h2>
      <p>
        Ask Claude or ChatGPT to open one of your Goalpost documents, and the
        document appears beside the conversation. Not a summary of it: the real
        editor you use in the Goalpost app. You can interact with it, search and edit your document, while your assistant works on the same
        draft from the chat.
      </p>
      <MediaVideo
        base={MCP_APP_DEMO_VIDEO}
        label="Claude opening a Goalpost document beside the conversation, adding items to the draft, and the person editing one of them in the document panel before submitting the draft for approval"
      />

      <p>
        For a while now, you've been able to use Goalpost's <Link to="/features/ai">AI integration</Link> which lets Claude, ChatGPT, or whatever AI agent harness you prefer interact with your Goalpost documents.
      </p>
      <p>
        This lets you use chat or voice chat to ask questions about your Goalpost documents, get summaries of content and status, and make changes on your behalf. Chat opens up a whole new world of interaction with your data, on Goalpost or everywhere else. But often an interactive, point-and-click interface and a rich, well-designed layout are still the best way to get the job done.
      </p>
      <p>
        Increasingly, AI harnesses (like Claude and ChatGPT) have included their own in-app browsers to interact with websites while you're chatting. This can bring the best of both worlds, but it can sometimes be difficult for AI to interact with a website designed for humans, so it can be slow or break down, and, frankly, things can get a little cramped packing a whole webpage in next to chat.
      </p>
      <p>
        Recently, we've <Link to="/blog/webmcp">made strides</Link> making it easier for AI to interact with our website itself.
      </p>
      <p>
        With our latest update, your AI agent can bring Goalpost's rich user interface right into your chat session. This lets it sit as part of your chat history and has closer integration with Claude, ChatGPT and other plugin ecosystems. You sign in once and it stays signed in. You can easily switch back and forth between interacting with it directly, and asking your agent to. None of these tools is always going to be the right way to interact with your data, so we are building for all of them.
      </p>

      <h2 id="technical">Technical detail</h2>
      <p>
        MCP, the <a href="https://modelcontextprotocol.io/">Model Context Protocol</a>, is an open standard that lets an AI
        assistant connect to outside tools and services like Goalpost, on your
        behalf and under your sign-in.
      </p>
      <p>
        <Link to="/blog/webmcp">WebMCP</Link>, support for which we previously announced, helps AI agents interact with full websites. WebMCP has no MCP server: the page itself exposes its tools through a well-known JavaScript object in the browser.
      </p>
      <p>
        <a href="https://modelcontextprotocol.io/extensions/apps/overview">MCP Apps</a> is a new capability for MCP servers that lets a service expose an iframe with rich UI content through the MCP server. Unlike WebMCP, this requires users to connect to the MCP server through a plugin system or connector, but from there the agent is able to leverage the authentication and discoverability of MCP tool calls.
      </p>
      <h2 id="try">How to try it</h2>
      <p>You need a Goalpost workspace on the Pro plan.</p>
      <p>
        <strong>Claude</strong> (desktop or claude.ai): <strong>Settings →
        Connectors → + → Add custom connector</strong>. Name:{' '}
        <strong>Goalpost</strong>. MCP server: <code>{mcpUrl}</code>. Click{' '}
        <strong>Continue</strong>, sign in on first use, then ask: “Show me{' '}
        <em>my document</em> in Goalpost.”
      </p>
      <p>
        <strong>ChatGPT</strong> (paid plan). On the web: <strong>Plugins →
        Add → Add custom MCP server</strong>. Name: <strong>Goalpost</strong>.
        Server URL: <code>{mcpUrl}</code>. Authentication:{' '}
        <strong>OAuth</strong>. Click <strong>Create as a plugin</strong>. In
        the desktop app: <strong>Settings → Plugins → MCPs → Add → Add MCP
        server</strong>, type <strong>Streamable HTTP</strong>, same URL,{' '}
        <strong>Save</strong>. Sign in on first use.
      </p>
      <p>
        <strong>Claude Code</strong>:
      </p>
      <div className="mkt-doc-codeblock">
        <pre className="mkt-doc-codeblock-content">{`claude mcp add goalpost --transport streamable-http ${mcpUrl}`}</pre>
      </div>
      <p>
        Codex and Cursor steps are in the{' '}
        <Link to="/docs/ai-integration">connection guide</Link>.
      </p>
      <p>
        The document opens beside the chat in Claude, ChatGPT, VS Code and other
        assistants that support MCP Apps. Terminal clients get the editing
        without the panel.
      </p>

      <h2 id="goalpost">New to Goalpost?</h2>
      <p>
        Goalpost is a shared place for people and AI assistants to write down
        what they are building. A document is a structured tree of sections
        and items, every edit is a versioned change that goes through approval,
        and approving is something only people do. The free plan covers
        everything except connecting an assistant. Start at{' '}
        <Link to="/docs">Getting Started</Link>, or{' '}
        <Link to="/register">sign up</Link>.
      </p>
    </BlogArticle>
  );
}
